// OAuth Callback Handler Page
// Receives authorization code from OAuth providers and exchanges for tokens
//
// ⚠️ IMPORTANT: This handles custom OAuth redirects (NOT Blink SDK)
// - Receives 'code' and 'state' query parameters from provider
// - Verifies state matches what was saved in localStorage (CSRF protection)
// - Exchanges code for tokens via backend edge function
// - Saves tokens to database via saveOAuthConnection()
// - Sends success message to parent window via postMessage
//
// This is called when:
// - Google redirects back with code after Gmail OAuth
// - Microsoft redirects back with code after Outlook OAuth

import { useEffect, useState } from 'react';
import { blink } from '@/lib/blink';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { saveOAuthConnection } from '@/lib/oauthConnections';

// 🔧 Using centralized Blink client from /lib/blink.ts
// This ensures OAuth callbacks don't interfere with the main app's auth state

// Determine OAuth exchange endpoint based on environment
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const OAUTH_EXCHANGE_ENDPOINT = isProduction
  ? 'https://ben3ou87--oauth-exchange.functions.blink.new'
  : 'http://localhost:8000/oauth-exchange';

export default function OAuthCallback() {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');
  const [provider, setProvider] = useState<string>('');

  useEffect(() => {
    // Add timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      if (status === 'processing') {
        setStatus('error');
        setMessage('OAuth processing timeout - please try again');
        
        // Send error to parent
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'oauth_error',
              error: 'OAuth processing timeout',
            },
            window.location.origin
          );
          setTimeout(() => window.close(), 2000);
        }
      }
    }, 30000); // 30 second timeout
    
    // Listen for window close events and notify parent
    // This helps the parent window know when the popup was closed manually
    const handleBeforeUnload = () => {
      if (window.opener && status === 'processing') {
        try {
          window.opener.postMessage(
            {
              type: 'oauth_popup_closed',
            },
            window.location.origin
          );
        } catch (e) {
          // Ignore errors - parent may already be gone
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    handleOAuthCallback().finally(() => clearTimeout(timeoutId));
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [status]);

  const handleOAuthCallback = async () => {
    try {
      console.log('🔐 OAuth Callback: Starting OAuth flow...');
      console.log('📍 Current URL:', window.location.href);
      
      // 🔧 CRITICAL FIX: Extract user ID from localStorage to avoid auth state interference
      // Do NOT call blink.auth.me() in the popup - it can interfere with parent window's auth
      // Instead, pass the user ID through localStorage during OAuth initiation
      
      // Parse URL parameters
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      // Check for OAuth errors
      if (error) {
        throw new Error(errorDescription || error);
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      // 🔧 ENHANCED: Try localStorage first, fallback to server storage
      let savedState: string | null = null;
      let savedProvider: string | null = null;
      let savedTimestamp: string | null = null;
      let savedUserId: string | null = null;
      let usingServerStorage = false;
      
      // Debug: Check localStorage accessibility
      let localStorageAccessible = true;
      try {
        const testKey = '__oauth_test_' + Date.now();
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        
        // Try localStorage first
        savedState = localStorage.getItem('oauth_state');
        savedProvider = localStorage.getItem('oauth_provider');
        savedTimestamp = localStorage.getItem('oauth_state_timestamp');
        savedUserId = localStorage.getItem('oauth_user_id');
      } catch (e) {
        localStorageAccessible = false;
        console.error('❌ localStorage is NOT accessible:', e);
      }
      
      // If localStorage failed or returned null, try server storage
      if (!savedState && state) {
        console.log('🔄 Attempting to retrieve OAuth state from server...');
        try {
          // Determine API endpoint
          const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
          const stateStoreEndpoint = isProduction
            ? 'https://ben3ou87--oauth-state-store.functions.blink.new'
            : 'http://localhost:8000/oauth-state-store';
          
          const response = await fetch(`${stateStoreEndpoint}?state=${encodeURIComponent(state)}`);
          if (response.ok) {
            const result = await response.json();
            savedState = result.data.state;
            savedProvider = result.data.provider;
            savedTimestamp = result.data.timestamp.toString();
            savedUserId = result.data.userId;
            usingServerStorage = true;
            console.log('✅ Retrieved OAuth state from server');
          }
        } catch (error) {
          console.error('❌ Failed to retrieve state from server:', error);
        }
      }
      
      console.log('🔐 OAuth Callback State Verification:');
      console.log('  Received state:', state ? state.substring(0, 16) + '...' : 'null');
      console.log('  Saved state:', savedState ? savedState.substring(0, 16) + '...' : 'null');
      console.log('  Saved provider:', savedProvider);
      console.log('  Saved timestamp:', savedTimestamp);
      console.log('  Saved user ID:', savedUserId);
      console.log('  State match:', savedState === state);
      console.log('  Storage method:', usingServerStorage ? 'Server-side' : 'localStorage');
      console.log('  localStorage accessible:', localStorageAccessible);
      console.log('');
      
      // Check if state was saved
      if (!savedState) {
        console.error('❌ No saved state found in localStorage!');
        console.error('  This can happen if:');
        console.error('  1. localStorage is disabled in browser settings');
        console.error('  2. Browser is in private/incognito mode (localStorage blocked)');
        console.error('  3. localStorage was cleared between initiation and callback');
        console.error('  4. OAuth was initiated in a different browser/tab');
        console.error('  5. Third-party cookie blocking is interfering');
        console.error('  6. Storage quota exceeded');
        console.error('');
        console.error('📋 To fix:');
        console.error('  • Check if your browser privacy settings allow localStorage');
        console.error('  • Try again in normal (non-private) mode');
        console.error('  • Clear browser cache/cookies and retry');
        console.error('  • Make sure OAuth popup and main window are in the same origin');
        throw new Error('OAuth state not found - please ensure localStorage is enabled in your browser. Try disabling private mode and enabling third-party cookies.');
      }
      
      // Verify state matches
      if (state !== savedState) {
        console.error('❌ State mismatch!');
        console.error('  Expected:', savedState);
        console.error('  Received:', state);
        console.error('  Possible causes:');
        console.error('  1. CSRF attack attempt');
        console.error('  2. Multiple OAuth flows initiated simultaneously');
        console.error('  3. Browser security settings interfering with localStorage');
        
        // Clear the invalid state
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_provider');
        localStorage.removeItem('oauth_state_timestamp');
        localStorage.removeItem('oauth_user_id');
        
        throw new Error('Invalid OAuth state - please try connecting again. If this persists, check your browser privacy settings.');
      }
      
      // Verify state hasn't expired (10 minute window)
      const timestamp = savedTimestamp;
      if (timestamp && Date.now() - parseInt(timestamp) > 10 * 60 * 1000) {
        const ageMinutes = Math.round((Date.now() - parseInt(timestamp)) / 60000);
        console.error(`❌ OAuth state expired! Age: ${ageMinutes} minutes (max 10)`);
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_provider');
        localStorage.removeItem('oauth_state_timestamp');
        localStorage.removeItem('oauth_user_id');
        throw new Error('OAuth session expired - please try connecting again');
      }

      // Get provider from localStorage
      const providerType = savedProvider;
      if (!providerType || (providerType !== 'gmail' && providerType !== 'outlook')) {
        console.error('❌ Invalid provider:', providerType);
        throw new Error('Invalid provider');
      }
      
      // 🔧 CRITICAL: Get user ID from localStorage (passed during OAuth initiation)
      if (!savedUserId) {
        console.error('❌ No user ID found in OAuth state!');
        throw new Error('Authentication session lost - please sign in again');
      }
      
      console.log('✅ State verification passed for provider:', providerType);
      console.log(`✅ User ID from OAuth state: ${savedUserId}`);
      console.log(`   Linking ${providerType} email account to this user...`);

      setProvider(providerType);
      setMessage(`Connecting to ${providerType === 'gmail' ? 'Gmail' : 'Outlook'}...`);

      console.log('Exchanging authorization code for tokens...');
      
      // Exchange code for tokens
      const response = await fetch(OAUTH_EXCHANGE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: providerType,
          code: code,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to exchange authorization code');
      }

      const data = await response.json();
      const { tokens } = data;

      console.log('Tokens received, saving to database...');

      // Save tokens and connection status to database using saved user ID
      await saveOAuthConnection(savedUserId, providerType, tokens.email || '', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: tokens.expiresAt,
      });

      setStatus('success');
      setMessage(`Successfully connected ${providerType === 'gmail' ? 'Gmail' : 'Outlook'}!`);

      // Clear OAuth state from storage (both methods)
      try {
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_provider');
        localStorage.removeItem('oauth_state_timestamp');
        localStorage.removeItem('oauth_user_id');
      } catch {
        // localStorage might not be accessible
      }
      
      // Clear from server if we used server storage
      if (usingServerStorage && state) {
        try {
          const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
          const stateStoreEndpoint = isProduction
            ? 'https://ben3ou87--oauth-state-store.functions.blink.new'
            : 'http://localhost:8000/oauth-state-store';
          
          await fetch(`${stateStoreEndpoint}?state=${encodeURIComponent(state)}`, {
            method: 'DELETE'
          });
          console.log('✅ Cleared OAuth state from server');
        } catch (error) {
          console.error('Failed to clear state from server:', error);
        }
      }

      // Send success message to opener window
      if (window.opener) {
        console.log('Sending success message to opener window...');
        window.opener.postMessage(
          {
            type: 'oauth_success',
            provider: providerType,
            tokens: tokens,
          },
          window.location.origin
        );

        // Wait for acknowledgment before closing
        const handleAck = (event: MessageEvent) => {
          if (event.origin === window.location.origin && 
              event.data.type === 'oauth_ack' && 
              event.data.provider === providerType) {
            console.log('Received acknowledgment, closing popup...');
            window.removeEventListener('message', handleAck);
            setTimeout(() => {
              window.close();
            }, 1000);
          }
        };
        
        window.addEventListener('message', handleAck);

        // Fallback: close after 5 seconds if no acknowledgment
        setTimeout(() => {
          console.log('No acknowledgment received, closing anyway...');
          window.removeEventListener('message', handleAck);
          window.close();
        }, 5000);
      } else {
        // If not in popup, redirect to settings
        setTimeout(() => {
          window.location.href = '/settings';
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OAuth authentication failed';
      
      // 🔧 FIX: Comprehensive error logging for debugging
      console.group('❌ OAuth Callback Error');
      console.error('Error:', error);
      console.error('Error Type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('Error Message:', errorMessage);
      console.error('Error Stack:', error instanceof Error ? error.stack : 'N/A');
      console.error('Current URL:', window.location.href);
      console.error('Timestamp:', new Date().toISOString());
      console.groupEnd();
      
      setStatus('error');
      setMessage(errorMessage);

      // 🔧 FIX: Send detailed error to parent window but don't force logout
      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              type: 'oauth_error',
              error: errorMessage,
              provider: provider || 'unknown',
              timestamp: new Date().toISOString(),
              // Don't trigger logout - just inform parent
              severity: 'warning'
            },
            window.location.origin
          );
          console.log('✅ Error message sent to parent window');
        } catch (postError) {
          console.error('❌ Failed to send error to parent:', postError);
        }

        // Close popup after showing error (give user time to read)
        setTimeout(() => {
          console.log('Closing OAuth popup after error...');
          window.close();
        }, 5000); // Increased to 5s so user can see the error
      }

      // Clear OAuth state (both methods)
      try {
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_provider');
        localStorage.removeItem('oauth_state_timestamp');
        localStorage.removeItem('oauth_user_id');
      } catch {
        // localStorage might not be accessible
      }
      
      // Clear from server if applicable
      if (state) {
        try {
          const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
          const stateStoreEndpoint = isProduction
            ? 'https://ben3ou87--oauth-state-store.functions.blink.new'
            : 'http://localhost:8000/oauth-state-store';
          
          await fetch(`${stateStoreEndpoint}?state=${encodeURIComponent(state)}`, {
            method: 'DELETE'
          });
        } catch {
          // Ignore cleanup errors
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status === 'processing' && (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Processing...
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Success!
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                Error
              </>
            )}
          </CardTitle>
          <CardDescription>
            {provider && `${provider === 'gmail' ? 'Gmail' : 'Outlook'} OAuth`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{message}</p>
          {status === 'success' && (
            <p className="text-xs text-muted-foreground mt-2">
              This window will close automatically...
            </p>
          )}
          {status === 'error' && !window.opener && (
            <p className="text-xs text-muted-foreground mt-2">
              <a href="/settings" className="text-primary hover:underline">
                Return to settings
              </a>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
